import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  DatePicker, 
  Select,
  Typography,
  Modal,
  message,
  Row,
  Col,
  Statistic,
  Dropdown
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  PrinterOutlined,
  MailOutlined,
  EyeOutlined,
  FileTextOutlined,
  TruckOutlined,
  MoreOutlined,
  ExportOutlined,
  ReloadOutlined,
  FilterOutlined,
  RollbackOutlined
} from '@ant-design/icons';
import type { SalesOrder, SalesFilters } from '../../types';
import apiService from '../../../../services/apiService';
import SalesOrderBuilder from '../SalesOrderBuilder';
import QuickReturnModal from '../QuickReturnModal';

const { Title } = Typography;
const { Option } = Select;



interface SalesOrderListProps {
  orders?: SalesOrder[];
  onOrderCreate?: (order: SalesOrder) => void;
  onOrderUpdate?: (order: SalesOrder) => void;
  onOrderDelete?: (orderId: string) => void;
}

export const SalesOrderList: React.FC<SalesOrderListProps> = ({
  orders: propOrders,
  onOrderCreate,
  onOrderUpdate,
  onOrderDelete
}) => {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [builderVisible, setBuilderVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null);
  const [builderMode, setBuilderMode] = useState<'create' | 'edit' | 'view'>('create');
  const [filters, setFilters] = useState<SalesFilters>({});
  const [quickReturnModalVisible, setQuickReturnModalVisible] = useState(false);
  const [selectedReturnOrder, setSelectedReturnOrder] = useState<SalesOrder | null>(null);

  const computePriority = (total: number): 'low' | 'normal' | 'high' | 'urgent' => {
    if (total >= 3000) return 'urgent';
    if (total >= 2000) return 'high';
    if (total >= 1000) return 'normal';
    return 'low';
  };

  // 移除 mock，統一使用後端 API 數據

  useEffect(() => {
    const load = async () => {
      try {
        if (propOrders && propOrders.length) {
          setOrders(propOrders);
          setFilteredOrders(propOrders);
          return;
        }
        setLoading(true);
        const resp: any = await apiService.getSalesOrdersV2({ page: 1, limit: 50 });
        const rows: any[] = Array.isArray(resp?.salesOrders) ? resp.salesOrders : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
        if (rows && rows.length) {
          const mapped: SalesOrder[] = rows.map((o: any) => ({
            id: String(o.id),
            orderNumber: o.orderNo || o.orderNumber,
            customerId: o.customerId,
            customer: o.customer || { id: o.customerId, name: o.customerName || 'Unknown Customer', email: '', phone: '', address: '' } as any,
            orderDate: o.orderDate || o.createdAt,
            deliveryDate: o.deliveryDate,
            status: String(o.status || 'DRAFT').toLowerCase(),
            items: (o.items || []).map((it: any) => ({ id: it.id, productId: it.productId, product: it.product, quantity: it.quantity, unitPrice: Number(it.unitPrice||0), discount: 0, discountType: 'percentage', taxRate: 0, lineTotal: Number(it.lineTotal||0) })),
            subtotal: Number(o.totalAmount || 0),
            taxAmount: 0,
            discountAmount: 0,
            shippingAmount: 0,
            totalAmount: Number(o.totalAmount || 0),
            currency: 'USD',
            warehouseId: '',
            warehouse: { id: '', name: '', code: '' } as any,
            paymentTerms: 'Net 30',
            priority: computePriority(Number(o.totalAmount || 0)),
            source: 'manual',
            createdBy: '',
            createdAt: o.createdAt,
            updatedAt: o.updatedAt,
            deliveryStatus: 'pending',
            invoiceStatus: 'none'
          }));
          setOrders(mapped);
          setFilteredOrders(mapped);
        }
      } catch (e) {
        message.error('加載訂單失敗');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [propOrders]);

  useEffect(() => {
    // Apply filters
    let filtered = orders;

    if (filters.search) {
      filtered = filtered.filter(order =>
        order.orderNumber.toLowerCase().includes(filters.search!.toLowerCase()) ||
        (order.customer.name && order.customer.name.toLowerCase().includes(filters.search!.toLowerCase()))
      );
    }

    if (filters.status) {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    if (filters.customer) {
      filtered = filtered.filter(order => order.customerId === filters.customer);
    }

    if (filters.dateRange) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderDate);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    if (filters.priority) {
      filtered = filtered.filter(order => order.priority === filters.priority);
    }

    setFilteredOrders(filtered);
  }, [filters, orders]);

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'default',
      confirmed: 'processing',
      processing: 'warning',
      shipped: 'blue',
      delivered: 'success',
      cancelled: 'error',
      invoiced: 'purple'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'green',
      normal: 'blue',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority as keyof typeof colors] || 'blue';
  };

  const getStatusText = (status: string) => {
    const map: Record<string, string> = {
      draft: '草稿',
      confirmed: '已確認',
      processing: '處理中',
      shipped: '已出貨',
      delivered: '已交付',
      cancelled: '已取消',
      invoiced: '已開立發票'
    };
    return map[status] || status;
  };

  const getPriorityText = (p: string) => ({ low: '低', normal: '普通', high: '高', urgent: '緊急' } as any)[p] || p;

  const columns = [
    {
      title: '訂單編號',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      sorter: true,
      fixed: 'left' as const,
      width: 260,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (text: string, record: SalesOrder) => (
        <a onClick={() => handleView(record)}>{text}</a>
      ),
    },
    {
      title: '客戶',
      key: 'customer',
      width: 100,
      render: (_: any, record: SalesOrder) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.customer.name || 'Unknown Customer'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.customer.contactPerson}
          </div>
        </div>
      ),
    },
    {
      title: '訂單日期',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      sorter: true,
      render: (date: Date) => new Date(date).toLocaleDateString(),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '優先級',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {getPriorityText(priority)}
        </Tag>
      ),
    },
    {
      title: '總金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      sorter: true,
      align: 'right' as const,
      render: (amount: number) => (
        <span style={{ fontWeight: 'bold' }}>
          ${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: '交付狀態',
      dataIndex: 'deliveryStatus',
      key: 'deliveryStatus',
      width: 120,
      render: (status: string) => {
        const colors = {
          pending: 'orange',
          partial: 'blue',
          delivered: 'green'
        };
        const textMap: Record<string, string> = { pending: '待出貨', partial: '部分', delivered: '已交付' };
        return (
          <Tag color={colors[status as keyof typeof colors] || 'default'}>
            {textMap[status] || status}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      fixed: 'right' as const,
      render: (_: any, record: SalesOrder) => {
        return (
          <Space size="small" wrap>
            <Button 
              type="text" 
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
            >
              詳情
            </Button>
            <Button 
              type="text" 
              icon={<PrinterOutlined />}
              onClick={() => handlePrint(record)}
              size="small"
            >
              打印
            </Button>
            <Button 
              type="text" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              disabled={record.status === 'delivered' || record.status === 'cancelled'}
            >
              編輯
            </Button>
          </Space>
        );
      },
    },
  ];

  const handleAdd = () => {
    setSelectedOrder(null);
    setBuilderMode('create');
    setBuilderVisible(true);
  };

  // 讀取詳情用於回顯（包含 items 等完整信息）
  const openOrder = async (record: SalesOrder, mode: 'view' | 'edit') => {
    try {
      setLoading(true);
      const resp: any = await apiService.getSalesOrderByIdV2(record.id);
      const o: any = resp?.salesOrder || resp || {};
      const mapped: SalesOrder = {
        id: String(o.id || record.id),
        orderNumber: o.orderNo || o.orderNumber || record.orderNumber,
        customerId: o.customerId || record.customerId,
        customer: o.customer || record.customer,
        orderDate: o.orderDate || o.createdAt || record.orderDate,
        deliveryDate: o.deliveryDate || record.deliveryDate,
        status: String(o.status || record.status || 'draft') as any,
        items: (o.items || record.items || []).map((it: any) => ({
          id: String(it.id || it.itemId || Date.now()),
          productId: String(it.productId),
          product: it.product || { id: String(it.productId), name: it.productName || '', sku: it.productSku || '' } as any,
          quantity: Number(it.quantity || 0),
          unitPrice: Number(it.unitPrice || 0),
          discount: 0,
          discountType: 'percentage',
          taxRate: 0,
          lineTotal: Number(it.lineTotal || (Number(it.unitPrice||0) * Number(it.quantity||0)))
        })),
        subtotal: Number(o.totalAmount || record.subtotal || 0),
        taxAmount: 0,
        discountAmount: 0,
        shippingAmount: 0,
        totalAmount: Number(o.totalAmount || record.totalAmount || 0),
        currency: 'USD',
        warehouseId: o.warehouseId || record.warehouseId || '',
        warehouse: o.warehouse || record.warehouse || ({ id: '', name: '' } as any),
        paymentTerms: o.paymentTerms || record.paymentTerms || 'Net 30',
        priority: (record.priority || 'normal') as any,
        source: record.source || 'manual',
        createdBy: record.createdBy || '',
        createdAt: o.createdAt || record.createdAt,
        updatedAt: o.updatedAt || record.updatedAt,
        deliveryStatus: record.deliveryStatus || 'pending',
        invoiceStatus: record.invoiceStatus || 'none',
        // 補回備註與條款（兩個鍵都嘗試）
        notes: o.notes || o.internalNotes || record.notes,
        terms: o.terms || record.terms
      };
      setSelectedOrder(mapped);
      setBuilderMode(mode);
      setBuilderVisible(true);
    } catch (e) {
      message.error('加載訂單詳情失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (record: SalesOrder) => openOrder(record, 'view');
  const handleEdit = (record: SalesOrder) => openOrder(record, 'edit');

  const handleCreateInvoice = (record: SalesOrder) => {
    Modal.confirm({
      title: 'Create Invoice',
      content: `Create invoice for ${record.orderNumber}?`,
      onOk: async () => {
        try {
          const auth = localStorage.getItem('auth-storage');
          const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
          const userId = parsed?.state?.user?.id || localStorage.getItem('current-user-id') || undefined;
          const companyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || undefined;
          await apiService.createInvoiceForOrderV2(record.id, { userId, companyId });
          message.success('Invoice created');
        } catch (e) { message.error('Create invoice failed'); }
      }
    });
  };

  const handleMarkShipped = (record: SalesOrder) => {
    Modal.confirm({
      title: 'Mark as Shipped',
      content: `Mark ${record.orderNumber} as shipped?`,
      onOk: async () => {
        try {
          await apiService.updateSalesOrderStatusV2(record.id,'SHIPPED');
          setOrders(prev => prev.map(o => o.id === record.id ? { ...o, status: 'shipped' as const, deliveryStatus: 'delivered' as const } : o));
          message.success(`Order ${record.orderNumber} marked as shipped`);
        } catch (e) { message.error('Mark shipped failed'); }
      }
    });
  };

  const handlePrint = (record: SalesOrder) => {
    window.print();
  };

  const handleEmail = (record: SalesOrder) => {
    message.info(`Emailing order ${record.orderNumber} to ${record.customer.email}`);
  };

  const handleQuickReturn = (record: SalesOrder) => {
    // 檢查訂單是否可以退貨
    if (record.status !== 'delivered' && record.status !== 'shipped') {
      message.warning('只有已交付或已發貨的訂單才能申請退貨');
      return;
    }
    
    setSelectedReturnOrder(record);
    setQuickReturnModalVisible(true);
  };

  const handleReturnCreated = (returnData: any) => {
    // TODO: 這里應該調用API創建退貨申請
    console.log('退貨申請創建:', returnData);
    
    // 模拟保存到退貨申請列表
    // 在實际應用中，這里会調用API保存到數據庫
    // 然后在退貨管理頁面顯示和處理
    
    // 更新訂單備注，標記有退貨申請
    const updatedOrders = orders.map(order => {
      if (order.id === returnData.originalOrderId) {
        return {
          ...order,
          notes: (order.notes || '') + `\n退貨申請: ${returnData.returnNumber} (待處理)`
        };
      }
      return order;
    });
    setOrders(updatedOrders);
    
    message.success(`退貨申請 ${returnData.returnNumber} 已創建，請前往退貨管理頁面進行處理`);
  };

  const handleDelete = (record: SalesOrder) => {
    Modal.confirm({
      title: 'Delete Sales Order',
      content: `Are you sure you want to delete order ${record.orderNumber}?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        return apiService.deleteSalesOrderV2(record.id)
          .then(() => {
            const updatedOrders = orders.filter(order => order.id !== record.id);
            setOrders(updatedOrders);
            message.success(`Order ${record.orderNumber} deleted successfully`);
            if (onOrderDelete) onOrderDelete(record.id);
          })
          .catch(() => message.error('Delete failed'));
      }
    });
  };

  const handleSaveOrder = (order: SalesOrder) => {
    if (builderMode === 'create') {
      const newOrders = [...orders, order];
      setOrders(newOrders);
      message.success('Sales order created successfully');
      
      if (onOrderCreate) {
        onOrderCreate(order);
      }
    } else if (builderMode === 'edit') {
      const updatedOrders = orders.map(o => o.id === order.id ? order : o);
      setOrders(updatedOrders);
      message.success('Sales order updated successfully');
      
      if (onOrderUpdate) {
        onOrderUpdate(order);
      }
    }
    
    setBuilderVisible(false);
    setSelectedOrder(null);
    // 以後端為準，保存成功後立即拉取最新列表，確保與資料庫同步
    handleRefresh();
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const resp: any = await apiService.getSalesOrdersV2({ page: 1, limit: 50, status: filters.status, search: filters.search, source: (filters as any).salesPlatform });
      const rows: any[] = Array.isArray(resp?.salesOrders) ? resp.salesOrders : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
      const mapped: SalesOrder[] = (rows||[]).map((o: any) => ({
        id: String(o.id),
        orderNumber: o.orderNo || o.orderNumber,
        customerId: o.customerId,
        customer: o.customer || { id: o.customerId, name: o.customerName || 'Unknown Customer' } as any,
        orderDate: o.orderDate || o.createdAt,
        deliveryDate: o.deliveryDate,
        status: String(o.status || 'DRAFT').toLowerCase(),
        items: (o.items || []).map((it: any) => ({ id: it.id, productId: it.productId, product: it.product, quantity: it.quantity, unitPrice: Number(it.unitPrice||0), discount: 0, discountType: 'percentage', taxRate: 0, lineTotal: Number(it.lineTotal||0) })),
        subtotal: Number(o.totalAmount || 0),
        taxAmount: 0,
        discountAmount: 0,
        shippingAmount: 0,
        totalAmount: Number(o.totalAmount || 0),
        currency: 'USD',
        warehouseId: '',
        warehouse: { id: '', name: '' } as any,
        paymentTerms: 'Net 30',
        priority: 'normal',
        source: 'manual',
        createdBy: '',
        createdAt: o.createdAt,
        updatedAt: o.updatedAt,
        deliveryStatus: 'pending',
        invoiceStatus: 'none'
      }));
      setOrders(mapped);
      setFilteredOrders(mapped);
      message.success('Orders refreshed');
    } catch (e) {
      message.error('Refresh failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    try {
      message.loading('正在導出訂單數據...', 0.5);
      
      // Prepare CSV data
      const csvHeaders = [
        '訂單編號',
        '客戶名称',
        '聯系人',
        '訂單日期',
        '交付日期',
        '狀態',
        '優先級',
        '小計',
        '税費',
        '折扣',
        '總金额',
        '付款条件',
        '倉庫',
        '交付狀態',
        '發票狀態',
        '創建時間'
      ];
      
      const csvData = filteredOrders.map(order => [
        order.orderNumber,
        order.customer.name || 'Unknown Customer',
        order.customer.contactPerson || '',
        `\u200B${new Date(order.orderDate).toISOString().split('T')[0]}`, // YYYY-MM-DD format
        order.deliveryDate ? `\u200B${new Date(order.deliveryDate).toISOString().split('T')[0]}` : '',
        order.status,
        order.priority,
        order.subtotal.toFixed(2),
        order.taxAmount.toFixed(2),
        order.discountAmount.toFixed(2),
        order.totalAmount.toFixed(2),
        order.paymentTerms,
        order.warehouse.name,
        order.deliveryStatus,
        order.invoiceStatus,
        `\u200B${new Date(order.createdAt).toISOString().split('T')[0]}` // YYYY-MM-DD format
      ]);
      
      // Convert to CSV format
      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => 
          row.map(field => {
            // Handle empty/null values
            if (field === null || field === undefined || field === '') {
              return '';
            }
            
            // Convert to string and handle special characters
            const stringField = String(field);
            
            // If field contains comma, quote, or newline, wrap in quotes and escape quotes
            if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n') || stringField.includes('\r')) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            
            return stringField;
          }).join(',')
        )
      ].join('\n');
      

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `銷售訂單_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        message.success(`成功導出 ${filteredOrders.length} 条訂單記錄`);
      }, 600);
      
    } catch (error) {
      console.error('Export error:', error);
      message.error('導出失敗，請重試');
    }
  };

  // Calculate statistics
  const statistics = {
    total: filteredOrders.length,
    draft: filteredOrders.filter(o => o.status === 'draft').length,
    confirmed: filteredOrders.filter(o => o.status === 'confirmed').length,
    processing: filteredOrders.filter(o => o.status === 'processing').length,
    shipped: filteredOrders.filter(o => o.status === 'shipped').length,
    delivered: filteredOrders.filter(o => o.status === 'delivered').length,
    totalValue: filteredOrders.reduce((sum, order) => sum + order.totalAmount, 0),
    avgOrderValue: filteredOrders.length > 0 ? filteredOrders.reduce((sum, order) => sum + order.totalAmount, 0) / filteredOrders.length : 0
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2}>銷售訂單</Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                重新整理
              </Button>
              <Button
                icon={<ExportOutlined />}
                onClick={handleExport}
              >
                匯出
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增訂單
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Statistics */}
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="訂單總數"
                value={statistics.total}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="已確認"
                value={statistics.confirmed}
                valueStyle={{ color: '#1890ff' }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="總金額"
                value={statistics.totalValue}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="平均訂單金額"
                value={statistics.avgOrderValue}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters and Search */}
        <Card>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="搜尋訂單或客戶"
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="選擇銷售渠道"
                style={{ width: '100%' }}
                value={(filters as any).salesPlatform}
                onChange={(value) => handleFilterChange('salesPlatform' as any, value)}
                allowClear
              >
                <Option value="HKTV Mall">HKTV Mall</Option>
                <Option value="Shopify">Shopify</Option>
                <Option value="Offline Store">Offline Store</Option>
                <Option value="Amazon">Amazon</Option>
                <Option value="eBay">eBay</Option>
                <Option value="Taobao">Taobao</Option>
                <Option value="Tmall">Tmall</Option>
                <Option value="JD.com">JD.com</Option>
                <Option value="Pinduoduo">Pinduoduo</Option>
                <Option value="Lazada">Lazada</Option>
                <Option value="Shopee">Shopee</Option>
                <Option value="Direct Sales">Direct Sales</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Select
                placeholder="狀態"
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
              >
                <Option value="draft">草稿</Option>
                <Option value="confirmed">已確認</Option>
                <Option value="processing">處理中</Option>
                <Option value="shipped">已出貨</Option>
                <Option value="delivered">已交付</Option>
                <Option value="cancelled">已取消</Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                onChange={(dates) => handleFilterChange('dateRange', dates ? [dates[0]?.toDate(), dates[1]?.toDate()] : null)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="優先級"
                style={{ width: '100%' }}
                value={filters.priority}
                onChange={(value) => handleFilterChange('priority', value)}
                allowClear
              >
                <Option value="低">低</Option>
                <Option value="normal">普通</Option>
                <Option value="high">高</Option>
                <Option value="urgent">緊急</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilters({})}
                disabled={Object.keys(filters).length === 0}
              >
                清除篩選
              </Button>
            </Col>
          </Row>

          {/* Orders Table */}
          <Table
            columns={columns}
            dataSource={filteredOrders}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1200 }}
            pagination={{
              total: filteredOrders.length,
              //pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100, 500],
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} 筆，共 ${total} 筆訂單`,
            }}
            size="small"
          />
        </Card>
      </Space>

      {/* Sales Order Builder Modal */}
      <Modal
        title={null}
        open={builderVisible}
        onCancel={() => {
          setBuilderVisible(false);
          setSelectedOrder(null);
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        destroyOnClose
      >
        <SalesOrderBuilder
          salesOrder={selectedOrder || undefined}
          mode={builderMode}
          onSave={handleSaveOrder}
          onCancel={() => {
            setBuilderVisible(false);
            setSelectedOrder(null);
          }}
        />
      </Modal>

      {/* Quick Return Modal */}
      <QuickReturnModal
        visible={quickReturnModalVisible}
        onCancel={() => {
          setQuickReturnModalVisible(false);
          setSelectedReturnOrder(null);
        }}
        order={selectedReturnOrder}
        onReturnCreated={handleReturnCreated}
      />
    </div>
  );
}; 